<template>
  <div class="container xl:px-8 p-4 mx-auto mt-8">
    <div class="grid grid-cols-12 gap-3">
      <div
        v-for="widget in widgets"
        @click="openPreview(widget)"
        class="2xl:col-span-3 xl:col-span-3 lg:col-span-4 xs:col-span-6 col-span-12 hover:bg-gradient-to-r hover:from-red-500 hover:via-pink-500 hover:to-orange-500 rounded-full hover:drop-shadow-lg p-1.5"
      >
        <div
          class="relative block p-4 shadow-xl border border-neutral-200 rounded-full bg-white cursor-pointer h-full"
        >
          <div class="flex items-center">
            <div class="w-12 h-12 relative rounded-full overflow-hidden">
              <img
                :src="
                  'https://cdn.qrdove.com/explores/' + widget.type_explore.icon
                "
                :alt="widget.type_explore.title"
                class="absolute  object-cover align-middle w-12 h-12"
              />
            </div>
            <h2
              class="text-lg ml-auto font-semibold text-gray-900 line-clamp-1"
            >
              {{
                getLabels(widget.type_explore.title) ||
                widget.type_explore.title
              }}
            </h2>
          </div>
          <p class="mt-4 sr-only">
            {{
              getDescription(
                widget.type_explore.title,
                widget.type_explore.description
              ) || widget.type_explore.description
            }}
          </p>
        </div>
      </div>
      <div
        @click="openLinkBuilder"
        class="2xl:col-span-3 xl:col-span-3 lg:col-span-4 xs:col-span-6 col-span-12 hover:bg-gradient-to-r hover:from-red-500 hover:via-pink-500 hover:to-orange-500 rounded-full hover:drop-shadow-lg p-1.5"
      >
        <div
          class="relative block p-4 shadow-xl rounded-full bg-white cursor-pointer h-full"
        >
          <div class="flex items-center">
            <img
              src="/icons/builder.svg"
              class="w-12 mr-auto"
              :alt="'page-builder'"
            />
            <h2
              class="text-lg ml-auto font-matter font-bold text-gray-900 line-clamp-1"
            >
              Page Builder
            </h2>
          </div>
        </div>
      </div>
    </div>
    <template v-if="widget_select">
      <PreviewWidgetDialog
        :widget="widget_select"
        :widget_content="{
          label: getLabels(widget_select.type_explore.title),
          description: getDescription(
            widget_select.type_explore.title,
            widget_select.type_explore.description
          ),
        }"
      />
    </template>
  </div>
</template>
<script lang="ts" setup>
import { storeToRefs } from "pinia";
import { useQrCreateStore } from "~~/stores/qrcreate";
import PreviewWidgetDialog from "../partials/PreviewWidgetDialog.vue";

interface ExploreType {
  type_explore: {
    id: number;
    title: string;
    description: string;
    group_explore_id: number;
    icon: string;
    status: number;
    full_widget: number;
  };
  name: string;
  image: string;
  uuid: string;
  slug: string;
}
const res = await useAsyncData(async () => {
  const res = await $fetch("/api/widget");
  return res as ExploreType[];
});
const widgets = res.data.value as ExploreType[];
const checkTag = (id: number) => {
  const news = [36, 37, 38, 39, 40, 41, 42, 43, 44];
  const hots = [15, 29, 17];
  if (news.includes(id)) {
    return "new";
  }
  if (hots.includes(id)) {
    return "hot";
  }
  return "";
};
const { preview_widget_modal } = storeToRefs(useQrCreateStore());
const widget_select = ref<ExploreType | null>(null);
const { redirectUrl } = useUtils();
const openPreview = (widget: ExploreType) => {
  if (typeof window !== "undefined") {
    const screen = window.innerWidth;
    if (screen < 768) {
      const url = "https://app.qrdove.com/sign-up?createSample=" + widget.uuid;
      redirectUrl(url);
      return;
    }
  }
  widget_select.value = widget;
  if (widget_select.value) {
    preview_widget_modal.value = true;
  }
};

const openLinkBuilder = () => {
  redirectUrl("https://app.qrdove.com/page-builder");
};

const widget_locales = ref({
  en: {
    labels: {
      whatsapp_chat: "WhatsApp Chat",
      click_to_call: "Click to Call",
      contact_details: "Contact Details",
      google_reviews: "Google Reviews",
      facebook_chat: "Facebook Chat",
      instagram_chat: "Instagram Chat",
      viber_chat: "Viber Chat",
      paypal: "PayPal",
      youtube: "YouTube",
      mobile_app: "Mobile App",
      restaurant_menu: "Restaurant Menu",
      pdf: "Pdf",
      facebook_review: "Facebook Review",
      instagram: "Instagram",
      event: "Event",
      spotify: "Spotify",
      facebook: "Facebook",
      image_gallery: "Image Gallery",
      faq: "FAQ",
      amazon_review: "Amazon Review",
      twitter: "Twitter",
      reddit: "Reddit",
      line_chat: "Line Chat",
      podcasts: "Podcasts",
      soundcloud: "Soundcloud",
      apple_music: "Apple Music",
      pinterest: "Pinterest",
      twitch: "Twitch",
      vimeo: "Vimeo",
      music: "Music",
      video: "Video",
      file_embed: "File Embed",
      contact_form: "Contact Form",
    },
    descriptions: {
      whatsapp_chat:
        "Give users an opportunity to contact you on WhatsApp straight from your qrcode",
      click_to_call:
        "Provide users with the fastest way to call you directly from your qrcode",
      contact_details: "Easily share downloadable contact details",
      google_reviews: "Elevate trustworthiness showing real clients’ reviews",
      facebook_chat:
        "Allow users to contact you on Facebook Messenger from your qrcode",
      instagram_chat:
        "Allow users to contact you on Instagram from your website",
      viber_chat: "Let users message you on Viber directly from your website",
      paypal: "Get paid and donation with PayPal",
      youtube: "Share YouTube videos on your qr code",
      mobile_app: "Promote a mobile app & get more downloads",
      restaurant_menu:
        "Present your dishes and drinks on your qrcode to attract more guests",
      pdf: "Easy sharing PDF for viewing and downloading with",
      facebook_review: "Display reviews from your Facebook page on your site",
      instagram: "Attract more Instagram followers",
      event: "Promote your event & create calendar invites effortlessly",
      spotify: "Share your latest or favorite music",
      facebook: "Add link or Facebook like button to your qr code",
      image_gallery: "Showcase NFTs you own on your qr code",
      faq: "Demonstrate the answers to the most common questions on your qrcode",
      amazon_review:
        "Share reviews about your products or store from Amazon on your site",
      twitter: "Showcase your tweets and Twitter feed",
      reddit: "Showcase your Reddit profile",
      line_chat: "Let users reach you out on Line right from your website",
      podcasts: "Get more podcast listeners and subscribers",
      soundcloud: "Get your music heard on SoundCloud",
      apple_music: "Share songs in itunes library",
      pinterest: "Showcase Pins, boards and more",
      twitch: "Get more stream viewers and engagement",
      vimeo: "Add Vimeo videos to your qr code",
      music: "Upload music and get more streams",
      video: "Upload videos and play them right",
      file_embed: "Embed files to be shared with your audience",
      contact_form: "Collect visitor contact info and messages",
    },
  },
  fr: {
    labels: {
      whatsapp_chat: "Chat WhatsApp",
      click_to_call: "Appel Direct",
      contact_details: "Coordonnées",
      google_reviews: "Avis Google",
      facebook_chat: "Chat Facebook",
      instagram_chat: "Chat Instagram",
      viber_chat: "Chat Viber",
      paypal: "PayPal",
      youtube: "YouTube",
      mobile_app: "Application Mobile",
      restaurant_menu: "Menu Restaurant",
      pdf: "PDF",
      facebook_review: "Avis Facebook",
      instagram: "Instagram",
      event: "Événement",
      spotify: "Spotify",
      facebook: "Facebook",
      image_gallery: "Galerie d'Images",
      faq: "FAQ",
      amazon_review: "Avis Amazon",
      twitter: "Twitter",
      reddit: "Reddit",
      line_chat: "Chat Line",
      podcasts: "Podcasts",
      soundcloud: "Soundcloud",
      apple_music: "Apple Music",
      pinterest: "Pinterest",
      twitch: "Twitch",
      vimeo: "Vimeo",
      music: "Musique",
      video: "Vidéo",
      file_embed: "Fichier Intégré",
      contact_form: "Formulaire de Contact",
    },
    descriptions: {
      whatsapp_chat:
        "Donnez aux utilisateurs la possibilité de vous contacter sur WhatsApp directement depuis votre qrcode",
      click_to_call:
        "Offrez aux utilisateurs le moyen le plus rapide de vous appeler directement depuis votre qrcode",
      contact_details: "Partagez facilement des coordonnées téléchargeables",
      google_reviews:
        "Renforcez la crédibilité en affichant les avis de vrais clients",
      facebook_chat:
        "Permettez aux utilisateurs de vous contacter sur Facebook Messenger depuis votre qrcode",
      instagram_chat:
        "Permettez aux utilisateurs de vous contacter sur Instagram depuis votre site web",
      viber_chat:
        "Permettez aux utilisateurs de vous envoyer un message sur Viber directement depuis votre site web",
      paypal: "Recevez des paiements et des dons avec PayPal",
      youtube: "Partagez des vidéos YouTube sur votre code QR",
      mobile_app:
        "Faites la promotion d'une application mobile et obtenez plus de téléchargements",
      restaurant_menu:
        "Présentez vos plats et boissons sur votre qrcode pour attirer plus de clients",
      pdf: "Partage facile de PDF pour la consultation et le téléchargement avec",
      facebook_review:
        "Affichez les avis de votre page Facebook sur votre site",
      instagram: "Attirez plus d'abonnés Instagram",
      event:
        "Faites la promotion de votre événement et créez facilement des invitations de calendrier",
      spotify: "Partagez votre dernière musique ou votre musique préférée",
      facebook: "Ajoutez un lien ou un bouton J'aime Facebook à votre code QR",
      image_gallery: "Présentez les NFT que vous possédez sur votre code QR",
      faq: "Présentez les réponses aux questions les plus fréquentes sur votre qrcode",
      amazon_review:
        "Partagez les avis sur vos produits ou votre magasin d'Amazon sur votre site",
      twitter: "Présentez vos tweets et votre fil Twitter",
      reddit: "Présentez votre profil Reddit",
      line_chat:
        "Permettez aux utilisateurs de vous joindre sur Line directement depuis votre site web",
      podcasts: "Obtenez plus d'auditeurs et d'abonnés de podcasts",
      soundcloud: "Faites entendre votre musique sur SoundCloud",
      apple_music: "Partagez des chansons dans la bibliothèque iTunes",
      pinterest: "Présentez les Pins, les tableaux et plus encore",
      twitch: "Obtenez plus de spectateurs de flux et d'engagement",
      vimeo: "Ajoutez des vidéos Vimeo à votre code QR",
      music: "Téléchargez de la musique et obtenez plus d'écoutes",
      video: "Téléchargez des vidéos et lisez-les directement",
      file_embed: "Intégrez des fichiers à partager avec votre public",
      contact_form:
        "Recueillez les informations de contact et les messages des visiteurs",
    },
  },
  it: {
    labels: {
      whatsapp_chat: "Chat WhatsApp",
      click_to_call: "Clicca per Chiamare",
      contact_details: "Dettagli di Contatto",
      google_reviews: "Recensioni Google",
      facebook_chat: "Chat Facebook",
      instagram_chat: "Chat Instagram",
      viber_chat: "Chat Viber",
      paypal: "PayPal",
      youtube: "YouTube",
      mobile_app: "App Mobile",
      restaurant_menu: "Menu Ristorante",
      pdf: "Pdf",
      facebook_review: "Recensione Facebook",
      instagram: "Instagram",
      event: "Evento",
      spotify: "Spotify",
      facebook: "Facebook",
      image_gallery: "Galleria di Immagini",
      faq: "FAQ",
      amazon_review: "Recensione Amazon",
      twitter: "Twitter",
      reddit: "Reddit",
      line_chat: "Chat Line",
      podcasts: "Podcast",
      soundcloud: "Soundcloud",
      apple_music: "Apple Music",
      pinterest: "Pinterest",
      twitch: "Twitch",
      vimeo: "Vimeo",
      music: "Musica",
      video: "Video",
      file_embed: "File Embed",
      contact_form: "Modulo di Contatto",
    },
    descriptions: {
      whatsapp_chat:
        "Offri agli utenti la possibilità di contattarti su WhatsApp direttamente dal tuo qrcode",
      click_to_call:
        "Fornisci agli utenti il modo più veloce per chiamarti direttamente dal tuo qrcode",
      contact_details:
        "Condividi facilmente i dettagli di contatto scaricabili",
      google_reviews:
        "Aumenta l'affidabilità mostrando le recensioni dei clienti reali",
      facebook_chat:
        "Consenti agli utenti di contattarti su Facebook Messenger dal tuo qrcode",
      instagram_chat:
        "Consenti agli utenti di contattarti su Instagram dal tuo sito web",
      viber_chat:
        "Consenti agli utenti di inviarti messaggi su Viber direttamente dal tuo sito web",
      paypal: "Ricevi pagamenti e donazioni con PayPal",
      youtube: "Condividi video di YouTube sul tuo codice QR",
      mobile_app: "Promuovi un'app mobile e ottieni più download",
      restaurant_menu:
        "Presenta i tuoi piatti e bevande sul tuo qrcode per attirare più ospiti",
      pdf: "Condivisione facile di PDF per la visualizzazione e il download con",
      facebook_review:
        "Visualizza le recensioni dalla tua pagina Facebook sul tuo sito",
      instagram: "Attira più follower di Instagram",
      event: "Promuovi il tuo evento e crea facilmente inviti al calendario",
      spotify: "Condividi la tua ultima musica o la tua musica preferita",
      facebook:
        "Aggiungi un link o un pulsante Mi Piace di Facebook al tuo codice QR",
      image_gallery: "Mostra i NFT che possiedi sul tuo codice QR",
      faq: "Mostra le risposte alle domande più comuni sul tuo qrcode",
      amazon_review:
        "Condividi recensioni sui tuoi prodotti o sul tuo negozio da Amazon sul tuo sito",
      twitter: "Mostra i tuoi tweet e il tuo feed Twitter",
      reddit: "Mostra il tuo profilo Reddit",
      line_chat:
        "Consenti agli utenti di contattarti su Line direttamente dal tuo sito web",
      podcasts: "Ottieni più ascoltatori e iscritti ai podcast",
      soundcloud: "Fai sentire la tua musica su SoundCloud",
      apple_music: "Condividi brani nella libreria iTunes",
      pinterest: "Mostra Pin, bacheche e altro",
      twitch: "Ottieni più spettatori di streaming e coinvolgimento",
      vimeo: "Aggiungi video Vimeo al tuo codice QR",
      music: "Carica musica e ottieni più streaming",
      video: "Carica video e riproducili direttamente",
      file_embed: "Incorpora file da condividere con il tuo pubblico",
      contact_form:
        "Raccogli le informazioni di contatto e i messaggi dei visitatori",
    },
  },
  de: {
    labels: {
      whatsapp_chat: "WhatsApp-Chat",
      click_to_call: "Zum Anrufen klicken",
      contact_details: "Kontaktdaten",
      google_reviews: "Google-Bewertungen",
      facebook_chat: "Facebook-Chat",
      instagram_chat: "Instagram-Chat",
      viber_chat: "Viber-Chat",
      paypal: "PayPal",
      youtube: "YouTube",
      mobile_app: "Mobile App",
      restaurant_menu: "Restaurantmenü",
      pdf: "PDF",
      facebook_review: "Facebook-Bewertung",
      instagram: "Instagram",
      event: "Veranstaltung",
      spotify: "Spotify",
      facebook: "Facebook",
      image_gallery: "Bildergalerie",
      faq: "FAQ",
      amazon_review: "Amazon-Bewertung",
      twitter: "Twitter",
      reddit: "Reddit",
      line_chat: "Line Chat",
      podcasts: "Podcasts",
      soundcloud: "Soundcloud",
      apple_music: "Apple Music",
      pinterest: "Pinterest",
      twitch: "Twitch",
      vimeo: "Vimeo",
      music: "Musik",
      video: "Video",
      file_embed: "Datei einbetten",
      contact_form: "Kontaktformular",
    },
    descriptions: {
      whatsapp_chat:
        "Geben Sie Benutzern die Möglichkeit, Sie über WhatsApp direkt von Ihrem QR-Code aus zu kontaktieren",
      click_to_call:
        "Bieten Sie Benutzern die schnellste Möglichkeit, Sie direkt von Ihrem QR-Code aus anzurufen",
      contact_details: "Teilen Sie einfach herunterladbare Kontaktdaten",
      google_reviews:
        "Erhöhen Sie die Vertrauenswürdigkeit, indem Sie Bewertungen echter Kunden anzeigen",
      facebook_chat:
        "Ermöglichen Sie es Benutzern, Sie über den Facebook Messenger von Ihrem QR-Code aus zu kontaktieren",
      instagram_chat:
        "Ermöglichen Sie es Benutzern, Sie über Instagram von Ihrer Website aus zu kontaktieren",
      viber_chat:
        "Lassen Sie Benutzer Sie über Viber direkt von Ihrer Website aus kontaktieren",
      paypal: "Lassen Sie sich mit PayPal bezahlen und Spenden zukommen",
      youtube: "Teilen Sie YouTube-Videos auf Ihrem QR-Code",
      mobile_app:
        "Bewerben Sie eine mobile App und erhalten Sie mehr Downloads",
      restaurant_menu:
        "Präsentieren Sie Ihre Gerichte und Getränke auf Ihrem QR-Code, um mehr Gäste anzuziehen",
      pdf: "Einfaches Teilen von PDF zur Ansicht und zum Herunterladen mit",
      facebook_review:
        "Zeigen Sie Bewertungen von Ihrer Facebook-Seite auf Ihrer Website an",
      instagram: "Gewinnen Sie mehr Instagram-Follower",
      event:
        "Bewerben Sie Ihre Veranstaltung und erstellen Sie mühelos Kalendereinladungen",
      spotify: "Teilen Sie Ihre neueste oder Lieblingsmusik",
      facebook:
        "Fügen Sie einen Link oder eine Facebook-Like-Schaltfläche zu Ihrem QR-Code hinzu",
      image_gallery: "Zeigen Sie NFTs, die Sie besitzen, auf Ihrem QR-Code",
      faq: "Demonstrieren Sie die Antworten auf die häufigsten Fragen auf Ihrem QR-Code",
      amazon_review:
        "Teilen Sie Bewertungen über Ihre Produkte oder Ihr Geschäft von Amazon auf Ihrer Website",
      twitter: "Zeigen Sie Ihre Tweets und Ihren Twitter-Feed",
      reddit: "Zeigen Sie Ihr Reddit-Profil",
      line_chat:
        "Lassen Sie Benutzer Sie direkt von Ihrer Website aus über Line erreichen",
      podcasts: "Gewinnen Sie mehr Podcast-Hörer und -Abonnenten",
      soundcloud: "Lassen Sie Ihre Musik auf SoundCloud hören",
      apple_music: "Teilen Sie Songs in der iTunes-Bibliothek",
      pinterest: "Zeigen Sie Pins, Boards und mehr",
      twitch: "Gewinnen Sie mehr Stream-Zuschauer und Engagement",
      vimeo: "Fügen Sie Vimeo-Videos zu Ihrem QR-Code hinzu",
      music: "Laden Sie Musik hoch und erhalten Sie mehr Streams",
      video: "Laden Sie Videos hoch und spielen Sie sie direkt ab",
      file_embed:
        "Binden Sie Dateien ein, die Sie mit Ihrem Publikum teilen möchten",
      contact_form:
        "Sammeln Sie Kontaktinformationen und Nachrichten von Besuchern",
    },
  },
  es: {
    labels: {
      whatsapp_chat: "Chat de WhatsApp",
      click_to_call: "Clic para Llamar",
      contact_details: "Datos de Contacto",
      google_reviews: "Reseñas de Google",
      facebook_chat: "Chat de Facebook",
      instagram_chat: "Chat de Instagram",
      viber_chat: "Chat de Viber",
      paypal: "PayPal",
      youtube: "YouTube",
      mobile_app: "Aplicación Móvil",
      restaurant_menu: "Menú de Restaurante",
      pdf: "PDF",
      facebook_review: "Reseña de Facebook",
      instagram: "Instagram",
      event: "Evento",
      spotify: "Spotify",
      facebook: "Facebook",
      image_gallery: "Galería de Imágenes",
      faq: "Preguntas Frecuentes",
      amazon_review: "Reseña de Amazon",
      twitter: "Twitter",
      reddit: "Reddit",
      line_chat: "Chat de Line",
      podcasts: "Podcasts",
      soundcloud: "Soundcloud",
      apple_music: "Apple Music",
      pinterest: "Pinterest",
      twitch: "Twitch",
      vimeo: "Vimeo",
      music: "Música",
      video: "Video",
      file_embed: "Archivo Incrustado",
      contact_form: "Formulario de Contacto",
    },
    descriptions: {
      whatsapp_chat:
        "Brinde a los usuarios la oportunidad de contactarlo en WhatsApp directamente desde su código QR",
      click_to_call:
        "Proporcione a los usuarios la forma más rápida de llamarlo directamente desde su código QR",
      contact_details: "Comparta fácilmente los datos de contacto descargables",
      google_reviews:
        "Eleve la confiabilidad mostrando las reseñas de clientes reales",
      facebook_chat:
        "Permita a los usuarios contactarlo en Facebook Messenger desde su código QR",
      instagram_chat:
        "Permita a los usuarios contactarlo en Instagram desde su sitio web",
      viber_chat:
        "Permita que los usuarios le envíen mensajes en Viber directamente desde su sitio web",
      paypal: "Reciba pagos y donaciones con PayPal",
      youtube: "Comparta videos de YouTube en su código QR",
      mobile_app: "Promocione una aplicación móvil y obtenga más descargas",
      restaurant_menu:
        "Presente sus platos y bebidas en su código QR para atraer a más clientes",
      pdf: "Compartir fácilmente PDF para ver y descargar con",
      facebook_review:
        "Muestre las reseñas de su página de Facebook en su sitio",
      instagram: "Atraiga más seguidores de Instagram",
      event:
        "Promocione su evento y cree invitaciones de calendario sin esfuerzo",
      spotify: "Comparta su música más reciente o favorita",
      facebook:
        "Agregue un enlace o un botón Me gusta de Facebook a su código QR",
      image_gallery: "Muestre los NFT que posee en su código QR",
      faq: "Demuestre las respuestas a las preguntas más comunes en su código QR",
      amazon_review:
        "Comparta reseñas sobre sus productos o tienda de Amazon en su sitio",
      twitter: "Muestre sus tweets y su feed de Twitter",
      reddit: "Muestre su perfil de Reddit",
      line_chat:
        "Permita que los usuarios se comuniquen con usted en Line directamente desde su sitio web",
      podcasts: "Consiga más oyentes y suscriptores de podcasts",
      soundcloud: "Haga que su música se escuche en SoundCloud",
      apple_music: "Comparta canciones en la biblioteca de iTunes",
      pinterest: "Muestre Pines, tableros y más",
      twitch: "Consiga más espectadores de transmisiones y participación",
      vimeo: "Agregue videos de Vimeo a su código QR",
      music: "Cargue música y obtenga más reproducciones",
      video: "Cargue videos y reprodúzcalos directamente",
      file_embed: "Incruste archivos para compartir con su público",
      contact_form:
        "Recopile información de contacto y mensajes de los visitantes",
    },
  },
  nl: {
    labels: {
      whatsapp_chat: "WhatsApp Chat",
      click_to_call: "Klik om te Bellen",
      contact_details: "Contactgegevens",
      google_reviews: "Google Reviews",
      facebook_chat: "Facebook Chat",
      instagram_chat: "Instagram Chat",
      viber_chat: "Viber Chat",
      paypal: "PayPal",
      youtube: "YouTube",
      mobile_app: "Mobiele App",
      restaurant_menu: "Restaurantmenu",
      pdf: "Pdf",
      facebook_review: "Facebook Review",
      instagram: "Instagram",
      event: "Evenement",
      spotify: "Spotify",
      facebook: "Facebook",
      image_gallery: "Afbeeldingengalerij",
      faq: "FAQ",
      amazon_review: "Amazon Review",
      twitter: "Twitter",
      reddit: "Reddit",
      line_chat: "Line Chat",
      podcasts: "Podcasts",
      soundcloud: "Soundcloud",
      apple_music: "Apple Music",
      pinterest: "Pinterest",
      twitch: "Twitch",
      vimeo: "Vimeo",
      music: "Muziek",
      video: "Video",
      file_embed: "Bestand Insluiten",
      contact_form: "Contactformulier",
    },
    descriptions: {
      whatsapp_chat:
        "Geef gebruikers de mogelijkheid om rechtstreeks vanuit uw qrcode contact met u op te nemen via WhatsApp",
      click_to_call:
        "Bied gebruikers de snelste manier om u rechtstreeks vanuit uw qrcode te bellen",
      contact_details: "Deel eenvoudig downloadbare contactgegevens",
      google_reviews:
        "Verhoog de betrouwbaarheid door beoordelingen van echte klanten te tonen",
      facebook_chat:
        "Sta gebruikers toe om contact met u op te nemen via Facebook Messenger vanuit uw qrcode",
      instagram_chat:
        "Sta gebruikers toe om contact met u op te nemen via Instagram vanaf uw website",
      viber_chat:
        "Laat gebruikers u rechtstreeks vanaf uw website een bericht sturen op Viber",
      paypal: "Word betaald en ontvang donaties met PayPal",
      youtube: "Deel YouTube-video's op uw qr-code",
      mobile_app: "Promoot een mobiele app en krijg meer downloads",
      restaurant_menu:
        "Presenteer uw gerechten en drankjes op uw qr-code om meer gasten aan te trekken",
      pdf: "Eenvoudig delen van PDF om te bekijken en te downloaden met",
      facebook_review: "Toon beoordelingen van uw Facebook-pagina op uw site",
      instagram: "Trek meer Instagram-volgers aan",
      event: "Promoot uw evenement en maak moeiteloos kalenderuitnodigingen",
      spotify: "Deel uw nieuwste of favoriete muziek",
      facebook:
        "Voeg een link of Facebook-vind ik leuk-knop toe aan uw qr-code",
      image_gallery: "Presenteer NFT's die u bezit op uw qr-code",
      faq: "Demonstreer de antwoorden op de meest voorkomende vragen op uw qr-code",
      amazon_review:
        "Deel beoordelingen over uw producten of winkel van Amazon op uw site",
      twitter: "Presenteer uw tweets en Twitter-feed",
      reddit: "Presenteer uw Reddit-profiel",
      line_chat:
        "Laat gebruikers u bereiken via Line rechtstreeks vanaf uw website",
      podcasts: "Krijg meer podcastluisteraars en -abonnees",
      soundcloud: "Laat uw muziek horen op SoundCloud",
      apple_music: "Deel nummers in de iTunes-bibliotheek",
      pinterest: "Presenteer Pins, borden en meer",
      twitch: "Krijg meer streamkijkers en betrokkenheid",
      vimeo: "Voeg Vimeo-video's toe aan uw qr-code",
      music: "Upload muziek en krijg meer streams",
      video: "Upload video's en speel ze direct af",
      file_embed: "Sluit bestanden in om met uw publiek te delen",
      contact_form: "Verzamel contactgegevens en berichten van bezoekers",
    },
  },
  pt: {
    labels: {
      whatsapp_chat: "Chat do WhatsApp",
      click_to_call: "Clique para Ligar",
      contact_details: "Detalhes de Contato",
      google_reviews: "Avaliações do Google",
      facebook_chat: "Chat do Facebook",
      instagram_chat: "Chat do Instagram",
      viber_chat: "Chat do Viber",
      paypal: "PayPal",
      youtube: "YouTube",
      mobile_app: "Aplicativo Móvel",
      restaurant_menu: "Cardápio do Restaurante",
      pdf: "Pdf",
      facebook_review: "Avaliação do Facebook",
      instagram: "Instagram",
      event: "Evento",
      spotify: "Spotify",
      facebook: "Facebook",
      image_gallery: "Galeria de Imagens",
      faq: "FAQ",
      amazon_review: "Avaliação da Amazon",
      twitter: "Twitter",
      reddit: "Reddit",
      line_chat: "Chat do Line",
      podcasts: "Podcasts",
      soundcloud: "Soundcloud",
      apple_music: "Apple Music",
      pinterest: "Pinterest",
      twitch: "Twitch",
      vimeo: "Vimeo",
      music: "Música",
      video: "Vídeo",
      file_embed: "Arquivo Incorporado",
      contact_form: "Formulário de Contato",
    },
    descriptions: {
      whatsapp_chat:
        "Dê aos usuários a oportunidade de entrar em contato com você no WhatsApp diretamente do seu qrcode",
      click_to_call:
        "Forneça aos usuários a maneira mais rápida de ligar para você diretamente do seu qrcode",
      contact_details:
        "Compartilhe facilmente detalhes de contato para download",
      google_reviews:
        "Eleve a confiabilidade mostrando avaliações de clientes reais",
      facebook_chat:
        "Permita que os usuários entrem em contato com você no Facebook Messenger a partir do seu qrcode",
      instagram_chat:
        "Permita que os usuários entrem em contato com você no Instagram a partir do seu site",
      viber_chat:
        "Permita que os usuários enviem mensagens para você no Viber diretamente do seu site",
      paypal: "Receba pagamentos e doações com o PayPal",
      youtube: "Compartilhe vídeos do YouTube no seu código QR",
      mobile_app: "Promova um aplicativo móvel e obtenha mais downloads",
      restaurant_menu:
        "Apresente seus pratos e bebidas em seu qrcode para atrair mais clientes",
      pdf: "Compartilhamento fácil de PDF para visualização e download com",
      facebook_review: "Exiba avaliações da sua página do Facebook no seu site",
      instagram: "Atraia mais seguidores do Instagram",
      event: "Promova seu evento e crie convites de calendário sem esforço",
      spotify: "Compartilhe suas músicas mais recentes ou favoritas",
      facebook: "Adicione um link ou botão Curtir do Facebook ao seu código QR",
      image_gallery: "Exiba os NFTs que você possui em seu código QR",
      faq: "Demonstre as respostas para as perguntas mais comuns em seu qrcode",
      amazon_review:
        "Compartilhe avaliações sobre seus produtos ou loja da Amazon no seu site",
      twitter: "Exiba seus tweets e o feed do Twitter",
      reddit: "Exiba seu perfil do Reddit",
      line_chat:
        "Permita que os usuários entrem em contato com você no Line diretamente do seu site",
      podcasts: "Obtenha mais ouvintes e assinantes de podcasts",
      soundcloud: "Faça sua música ser ouvida no SoundCloud",
      apple_music: "Compartilhe músicas na biblioteca do iTunes",
      pinterest: "Exiba Pins, quadros e muito mais",
      twitch: "Obtenha mais espectadores de transmissões e engajamento",
      vimeo: "Adicione vídeos do Vimeo ao seu código QR",
      music: "Carregue músicas e obtenha mais transmissões",
      video: "Carregue vídeos e reproduza-os diretamente",
      file_embed: "Incorpore arquivos para compartilhar com seu público",
      contact_form: "Colete informações de contato e mensagens dos visitantes",
    },
  },
  ja: {
    labels: {
      whatsapp_chat: "WhatsAppチャット",
      click_to_call: "クリックして電話",
      contact_details: "連絡先の詳細",
      google_reviews: "Googleレビュー",
      facebook_chat: "Facebookチャット",
      instagram_chat: "Instagramチャット",
      viber_chat: "Viberチャット",
      paypal: "PayPal",
      youtube: "YouTube",
      mobile_app: "モバイルアプリ",
      restaurant_menu: "レストランメニュー",
      pdf: "PDF",
      facebook_review: "Facebookレビュー",
      instagram: "Instagram",
      event: "イベント",
      spotify: "Spotify",
      facebook: "Facebook",
      image_gallery: "画像ギャラリー",
      faq: "FAQ",
      amazon_review: "Amazonレビュー",
      twitter: "Twitter",
      reddit: "Reddit",
      line_chat: "Lineチャット",
      podcasts: "ポッドキャスト",
      soundcloud: "SoundCloud",
      apple_music: "Apple Music",
      pinterest: "Pinterest",
      twitch: "Twitch",
      vimeo: "Vimeo",
      music: "音楽",
      video: "ビデオ",
      file_embed: "ファイルの埋め込み",
      contact_form: "お問い合わせフォーム",
    },
    descriptions: {
      whatsapp_chat:
        "ユーザーがQRコードから直接WhatsAppで連絡できるようにします",
      click_to_call:
        "ユーザーがQRコードから直接電話をかけるための最速の方法を提供します",
      contact_details: "ダウンロード可能な連絡先情報を簡単に共有します",
      google_reviews: "実際の顧客のレビューを表示して信頼性を高めます",
      facebook_chat:
        "ユーザーがQRコードからFacebook Messengerで連絡できるようにします",
      instagram_chat:
        "ユーザーがウェブサイトからInstagramで連絡できるようにします",
      viber_chat:
        "ユーザーがウェブサイトから直接Viberでメッセージを送信できるようにします",
      paypal: "PayPalで支払いと寄付を受け付けます",
      youtube: "QRコードでYouTube動画を共有します",
      mobile_app: "モバイルアプリを宣伝し、ダウンロード数を増やします",
      restaurant_menu:
        "QRコードで料理と飲み物を紹介し、より多くのゲストを惹きつけます",
      pdf: "PDFを簡単に共有して表示およびダウンロードできるようにします",
      facebook_review: "サイトでFacebookページのレビューを表示します",
      instagram: "Instagramのフォロワーを増やします",
      event: "イベントを宣伝し、カレンダーの招待状を簡単に作成します",
      spotify: "最新のお気に入りの音楽を共有します",
      facebook: "QRコードにリンクまたはFacebookいいね！ボタンを追加します",
      image_gallery: "QRコードで所有しているNFTを紹介します",
      faq: "QRコードで最も一般的な質問への回答を示します",
      amazon_review:
        "Amazonの製品またはストアに関するレビューをサイトで共有します",
      twitter: "ツイートとTwitterフィードを紹介します",
      reddit: "Redditプロフィールを紹介します",
      line_chat: "ユーザーがウェブサイトから直接Lineで連絡できるようにします",
      podcasts: "ポッドキャストのリスナーとサブスクライバーを増やします",
      soundcloud: "SoundCloudで音楽を聴いてもらいましょう",
      apple_music: "iTunesライブラリで曲を共有します",
      pinterest: "ピン、ボードなどを紹介します",
      twitch: "ストリーム視聴者とエンゲージメントを増やします",
      vimeo: "QRコードにVimeoビデオを追加します",
      music: "音楽をアップロードしてストリームを増やします",
      video: "動画をアップロードして直接再生します",
      file_embed: "視聴者と共有するファイルを埋め込みます",
      contact_form: "訪問者の連絡先情報とメッセージを収集します",
    },
  },
  zh: {
    labels: {
      whatsapp_chat: "WhatsApp聊天",
      click_to_call: "点击通话",
      contact_details: "联系方式",
      google_reviews: "谷歌评论",
      facebook_chat: "Facebook聊天",
      instagram_chat: "Instagram聊天",
      viber_chat: "Viber聊天",
      paypal: "PayPal",
      youtube: "YouTube",
      mobile_app: "移动应用",
      restaurant_menu: "餐厅菜单",
      pdf: "PDF",
      facebook_review: "Facebook评论",
      instagram: "Instagram",
      event: "活动",
      spotify: "Spotify",
      facebook: "Facebook",
      image_gallery: "图片库",
      faq: "常见问题解答",
      amazon_review: "亚马逊评论",
      twitter: "Twitter",
      reddit: "Reddit",
      line_chat: "Line聊天",
      podcasts: "播客",
      soundcloud: "Soundcloud",
      apple_music: "Apple Music",
      pinterest: "Pinterest",
      twitch: "Twitch",
      vimeo: "Vimeo",
      music: "音乐",
      video: "视频",
      file_embed: "文件嵌入",
      contact_form: "联系表格",
    },
    descriptions: {
      whatsapp_chat: "让用户可以直接从您的二维码上在 WhatsApp 联系您",
      click_to_call: "为用户提供最快的方式直接从您的二维码上给您打电话",
      contact_details: "轻松分享可下载的联系方式",
      google_reviews: "通过展示真实客户的评论来提升信任度",
      facebook_chat: "允许用户通过您的二维码在 Facebook Messenger 上联系您",
      instagram_chat: "允许用户从您的网站在 Instagram 上联系您",
      viber_chat: "让用户直接从您的网站上在 Viber 上给您发消息",
      paypal: "使用 PayPal 收款和捐款",
      youtube: "在您的二维码上分享 YouTube 视频",
      mobile_app: "推广移动应用并获得更多下载量",
      restaurant_menu: "在您的二维码上展示您的菜肴和饮料，以吸引更多客人",
      pdf: "轻松共享 PDF 以供查看和下载",
      facebook_review: "在您的网站上显示来自您 Facebook 页面的评论",
      instagram: "吸引更多 Instagram 粉丝",
      event: "推广您的活动并轻松创建日历邀请",
      spotify: "分享您最新或最喜欢的音乐",
      facebook: "将链接或 Facebook 点赞按钮添加到您的二维码",
      image_gallery: "展示您在二维码上拥有的 NFT",
      faq: "在您的二维码上展示最常见问题的答案",
      amazon_review: "在您的网站上分享来自亚马逊的关于您的产品或商店的评论",
      twitter: "展示您的推文和 Twitter 订阅源",
      reddit: "展示您的 Reddit 个人资料",
      line_chat: "让用户直接从您的网站在 Line 上联系您",
      podcasts: "获得更多的播客听众和订阅者",
      soundcloud: "在 SoundCloud 上听到您的音乐",
      apple_music: "在 iTunes 库中分享歌曲",
      pinterest: "展示 Pin 图、版块等",
      twitch: "获得更多流媒体观众和互动",
      vimeo: "将 Vimeo 视频添加到您的二维码",
      music: "上传音乐并获得更多播放量",
      video: "上传视频并立即播放",
      file_embed: "嵌入文件以与您的受众共享",
      contact_form: "收集访客联系信息和消息",
    },
  },
  vi: {
    labels: {
      whatsapp_chat: "Trò chuyện WhatsApp",
      click_to_call: "Nhấp để gọi",
      contact_details: "Chi tiết liên hệ",
      google_reviews: "Đánh giá trên Google",
      facebook_chat: "Trò chuyện Facebook",
      instagram_chat: "Trò chuyện Instagram",
      viber_chat: "Trò chuyện Viber",
      paypal: "PayPal",
      youtube: "YouTube",
      mobile_app: "Ứng dụng di động",
      restaurant_menu: "Thực đơn nhà hàng",
      pdf: "PDF",
      facebook_review: "Đánh giá trên Facebook",
      instagram: "Instagram",
      event: "Sự kiện",
      spotify: "Spotify",
      facebook: "Facebook",
      image_gallery: "Thư viện ảnh",
      faq: "Câu hỏi thường gặp",
      amazon_review: "Đánh giá trên Amazon",
      twitter: "Twitter",
      reddit: "Reddit",
      line_chat: "Trò chuyện Line",
      podcasts: "Podcast",
      soundcloud: "SoundCloud",
      apple_music: "Apple Music",
      pinterest: "Pinterest",
      twitch: "Twitch",
      vimeo: "Vimeo",
      music: "Âm nhạc",
      video: "Video",
      file_embed: "Nhúng tệp",
      contact_form: "Biểu mẫu liên hệ",
    },
    descriptions: {
      whatsapp_chat:
        "Cho phép người dùng liên hệ với bạn trên WhatsApp trực tiếp từ mã QR của bạn",
      click_to_call:
        "Cung cấp cho người dùng cách nhanh nhất để gọi cho bạn trực tiếp từ mã QR của bạn",
      contact_details: "Dễ dàng chia sẻ thông tin liên hệ có thể tải xuống",
      google_reviews:
        "Nâng cao độ tin cậy bằng cách hiển thị đánh giá của khách hàng thực",
      facebook_chat:
        "Cho phép người dùng liên hệ với bạn trên Facebook Messenger từ mã QR của bạn",
      instagram_chat:
        "Cho phép người dùng liên hệ với bạn trên Instagram từ trang web của bạn",
      viber_chat:
        "Cho phép người dùng nhắn tin cho bạn trên Viber trực tiếp từ trang web của bạn",
      paypal: "Nhận thanh toán và quyên góp bằng PayPal",
      youtube: "Chia sẻ video YouTube trên mã QR của bạn",
      mobile_app: "Quảng bá ứng dụng di động và nhận thêm lượt tải xuống",
      restaurant_menu:
        "Trình bày các món ăn và đồ uống của bạn trên mã QR để thu hút thêm khách",
      pdf: "Dễ dàng chia sẻ PDF để xem và tải xuống",
      facebook_review:
        "Hiển thị đánh giá từ trang Facebook của bạn trên trang web của bạn",
      instagram: "Thu hút thêm người theo dõi Instagram",
      event:
        "Quảng bá sự kiện của bạn và tạo lời mời trên lịch một cách dễ dàng",
      spotify: "Chia sẻ nhạc mới nhất hoặc yêu thích của bạn",
      facebook: "Thêm liên kết hoặc nút thích Facebook vào mã QR của bạn",
      image_gallery: "Trưng bày NFT bạn sở hữu trên mã QR của bạn",
      faq: "Trình bày các câu trả lời cho các câu hỏi phổ biến nhất trên mã QR của bạn",
      amazon_review:
        "Chia sẻ đánh giá về sản phẩm hoặc cửa hàng của bạn từ Amazon trên trang web của bạn",
      twitter: "Hiển thị các tweet và nguồn cấp dữ liệu Twitter của bạn",
      reddit: "Hiển thị hồ sơ Reddit của bạn",
      line_chat:
        "Cho phép người dùng liên hệ với bạn trên Line ngay từ trang web của bạn",
      podcasts: "Nhận thêm người nghe và đăng ký podcast",
      soundcloud: "Đưa nhạc của bạn được nghe trên SoundCloud",
      apple_music: "Chia sẻ bài hát trong thư viện iTunes",
      pinterest: "Trưng bày Ghim, bảng và hơn thế nữa",
      twitch: "Nhận thêm người xem và tương tác luồng",
      vimeo: "Thêm video Vimeo vào mã QR của bạn",
      music: "Tải nhạc lên và nhận thêm lượt phát",
      video: "Tải video lên và phát chúng ngay",
      file_embed: "Nhúng tệp để chia sẻ với khán giả của bạn",
      contact_form: "Thu thập thông tin liên hệ và tin nhắn của khách truy cập",
    },
  },
  id: {
    labels: {
      whatsapp_chat: "Chat WhatsApp",
      click_to_call: "Klik untuk Menelepon",
      contact_details: "Detail Kontak",
      google_reviews: "Ulasan Google",
      facebook_chat: "Chat Facebook",
      instagram_chat: "Chat Instagram",
      viber_chat: "Chat Viber",
      paypal: "PayPal",
      youtube: "YouTube",
      mobile_app: "Aplikasi Seluler",
      restaurant_menu: "Menu Restoran",
      pdf: "PDF",
      facebook_review: "Ulasan Facebook",
      instagram: "Instagram",
      event: "Acara",
      spotify: "Spotify",
      facebook: "Facebook",
      image_gallery: "Galeri Gambar",
      faq: "FAQ",
      amazon_review: "Ulasan Amazon",
      twitter: "Twitter",
      reddit: "Reddit",
      line_chat: "Chat Line",
      podcasts: "Podcast",
      soundcloud: "SoundCloud",
      apple_music: "Apple Music",
      pinterest: "Pinterest",
      twitch: "Twitch",
      vimeo: "Vimeo",
      music: "Musik",
      video: "Video",
      file_embed: "Sematkan File",
      contact_form: "Formulir Kontak",
    },
    descriptions: {
      whatsapp_chat:
        "Beri pengguna kesempatan untuk menghubungi Anda di WhatsApp langsung dari kode QR Anda",
      click_to_call:
        "Berikan pengguna cara tercepat untuk menghubungi Anda langsung dari kode QR Anda",
      contact_details: "Bagikan detail kontak yang mudah diunduh",
      google_reviews:
        "Tingkatkan kepercayaan dengan menampilkan ulasan klien asli",
      facebook_chat:
        "Izinkan pengguna untuk menghubungi Anda di Facebook Messenger dari kode QR Anda",
      instagram_chat:
        "Izinkan pengguna untuk menghubungi Anda di Instagram dari situs web Anda",
      viber_chat:
        "Biarkan pengguna mengirimi Anda pesan di Viber langsung dari situs web Anda",
      paypal: "Dapatkan bayaran dan donasi dengan PayPal",
      youtube: "Bagikan video YouTube di kode QR Anda",
      mobile_app: "Promosikan aplikasi seluler & dapatkan lebih banyak unduhan",
      restaurant_menu:
        "Sajikan hidangan dan minuman Anda di kode QR untuk menarik lebih banyak tamu",
      pdf: "Berbagi PDF dengan mudah untuk dilihat dan diunduh",
      facebook_review:
        "Tampilkan ulasan dari halaman Facebook Anda di situs Anda",
      instagram: "Tarik lebih banyak pengikut Instagram",
      event: "Promosikan acara Anda & buat undangan kalender dengan mudah",
      spotify: "Bagikan musik terbaru atau favorit Anda",
      facebook: "Tambahkan tautan atau tombol suka Facebook ke kode QR Anda",
      image_gallery: "Pamerkan NFT yang Anda miliki di kode QR Anda",
      faq: "Tunjukkan jawaban atas pertanyaan paling umum di kode QR Anda",
      amazon_review:
        "Bagikan ulasan tentang produk atau toko Anda dari Amazon di situs Anda",
      twitter: "Tampilkan tweet dan umpan Twitter Anda",
      reddit: "Pamerkan profil Reddit Anda",
      line_chat:
        "Biarkan pengguna menghubungi Anda di Line langsung dari situs web Anda",
      podcasts: "Dapatkan lebih banyak pendengar dan pelanggan podcast",
      soundcloud: "Dengarkan musik Anda di SoundCloud",
      apple_music: "Bagikan lagu di perpustakaan iTunes",
      pinterest: "Pamerkan Pin, papan, dan lainnya",
      twitch: "Dapatkan lebih banyak penonton streaming dan keterlibatan",
      vimeo: "Tambahkan video Vimeo ke kode QR Anda",
      music: "Unggah musik dan dapatkan lebih banyak streaming",
      video: "Unggah video dan putar langsung",
      file_embed: "Sematkan file untuk dibagikan dengan audiens Anda",
      contact_form: "Kumpulkan info kontak dan pesan pengunjung",
    },
  },
  tr: {
    labels: {
      whatsapp_chat: "WhatsApp Sohbet",
      click_to_call: "Tıklayarak Ara",
      contact_details: "İletişim Bilgileri",
      google_reviews: "Google Yorumları",
      facebook_chat: "Facebook Sohbet",
      instagram_chat: "Instagram Sohbet",
      viber_chat: "Viber Sohbet",
      paypal: "PayPal",
      youtube: "YouTube",
      mobile_app: "Mobil Uygulama",
      restaurant_menu: "Restoran Menüsü",
      pdf: "PDF",
      facebook_review: "Facebook Yorumu",
      instagram: "Instagram",
      event: "Etkinlik",
      spotify: "Spotify",
      facebook: "Facebook",
      image_gallery: "Resim Galerisi",
      faq: "SSS",
      amazon_review: "Amazon İncelemesi",
      twitter: "Twitter",
      reddit: "Reddit",
      line_chat: "Line Sohbet",
      podcasts: "Podcastler",
      soundcloud: "Soundcloud",
      apple_music: "Apple Müzik",
      pinterest: "Pinterest",
      twitch: "Twitch",
      vimeo: "Vimeo",
      music: "Müzik",
      video: "Video",
      file_embed: "Dosya Yerleştirme",
      contact_form: "İletişim Formu",
    },
    descriptions: {
      whatsapp_chat:
        "Kullanıcılara doğrudan QR kodunuzdan WhatsApp'ta sizinle iletişim kurma fırsatı verin",
      click_to_call:
        "Kullanıcılara doğrudan QR kodunuzdan sizi aramanın en hızlı yolunu sağlayın",
      contact_details: "İndirilebilir iletişim bilgilerini kolayca paylaşın",
      google_reviews:
        "Gerçek müşteri yorumlarını göstererek güvenilirliği artırın",
      facebook_chat:
        "Kullanıcıların QR kodunuzdan Facebook Messenger'da sizinle iletişim kurmasına izin verin",
      instagram_chat:
        "Kullanıcıların web sitenizden Instagram'da sizinle iletişim kurmasına izin verin",
      viber_chat:
        "Kullanıcıların doğrudan web sitenizden Viber'da size mesaj göndermesine izin verin",
      paypal: "PayPal ile ödeme alın ve bağış yapın",
      youtube: "QR kodunuzda YouTube videoları paylaşın",
      mobile_app: "Bir mobil uygulamayı tanıtın ve daha fazla indirme alın",
      restaurant_menu:
        "Daha fazla misafir çekmek için yemeklerinizi ve içeceklerinizi QR kodunuzda sunun",
      pdf: "Görüntüleme ve indirme için kolay PDF paylaşımı",
      facebook_review:
        "Web sitenizde Facebook sayfanızdan yorumları görüntüleyin",
      instagram: "Daha fazla Instagram takipçisi çekin",
      event: "Etkinliğinizi tanıtın ve zahmetsizce takvim davetleri oluşturun",
      spotify: "En yeni veya favori müziğinizi paylaşın",
      facebook: "QR kodunuza bağlantı veya Facebook beğenme düğmesi ekleyin",
      image_gallery: "QR kodunuzda sahip olduğunuz NFT'leri sergileyin",
      faq: "QR kodunuzda en yaygın soruların cevaplarını gösterin",
      amazon_review:
        "Web sitenizde Amazon'dan ürünleriniz veya mağazanız hakkında yorumları paylaşın",
      twitter: "Tweetlerinizi ve Twitter akışınızı sergileyin",
      reddit: "Reddit profilinizi sergileyin",
      line_chat:
        "Kullanıcıların doğrudan web sitenizden Line'da size ulaşmasına izin verin",
      podcasts: "Daha fazla podcast dinleyicisi ve abonesi edinin",
      soundcloud: "Müziğinizin SoundCloud'da duyulmasını sağlayın",
      apple_music: "iTunes kitaplığındaki şarkıları paylaşın",
      pinterest: "Pinleri, panoları ve daha fazlasını sergileyin",
      twitch: "Daha fazla yayın görüntüleyicisi ve etkileşim edinin",
      vimeo: "QR kodunuza Vimeo videoları ekleyin",
      music: "Müzik yükleyin ve daha fazla yayın alın",
      video: "Video yükleyin ve hemen oynatın",
      file_embed: "Hedef kitlenizle paylaşılacak dosyaları yerleştirin",
      contact_form: "Ziyaretçi iletişim bilgilerini ve mesajlarını toplayın",
    },
  },
  hi: {
    labels: {
      whatsapp_chat: "व्हाट्सएप चैट",
      click_to_call: "कॉल करने के लिए क्लिक करें",
      contact_details: "संपर्क विवरण",
      google_reviews: "गूगल समीक्षाएँ",
      facebook_chat: "फेसबुक चैट",
      instagram_chat: "इंस्टाग्राम चैट",
      viber_chat: "वाइबर चैट",
      paypal: "पेपैल",
      youtube: "यूट्यूब",
      mobile_app: "मोबाइल ऐप",
      restaurant_menu: "रेस्तरां मेनू",
      pdf: "पीडीएफ",
      facebook_review: "फेसबुक समीक्षा",
      instagram: "इंस्टाग्राम",
      event: "इवेंट",
      spotify: "स्पॉटिफाई",
      facebook: "फेसबुक",
      image_gallery: "छवि गैलरी",
      faq: "अक्सर पूछे जाने वाले प्रश्न",
      amazon_review: "अमेज़ॅन समीक्षा",
      twitter: "ट्विटर",
      reddit: "रेडिट",
      line_chat: "लाइन चैट",
      podcasts: "पॉडकास्ट",
      soundcloud: "साउंडक्लाउड",
      apple_music: "एप्पल म्यूजिक",
      pinterest: "पिनटेरेस्ट",
      twitch: "ट्विच",
      vimeo: "विमियो",
      music: "संगीत",
      video: "वीडियो",
      file_embed: "फ़ाइल एम्बेड करें",
      contact_form: "संपर्क फ़ॉर्म",
    },
    descriptions: {
      whatsapp_chat:
        "उपयोगकर्ताओं को सीधे अपने क्यूआर कोड से व्हाट्सएप पर आपसे संपर्क करने का अवसर दें",
      click_to_call:
        "उपयोगकर्ताओं को सीधे अपने क्यूआर कोड से आपको कॉल करने का सबसे तेज़ तरीका प्रदान करें",
      contact_details: "आसानी से डाउनलोड करने योग्य संपर्क विवरण साझा करें",
      google_reviews:
        "वास्तविक ग्राहकों की समीक्षाएँ दिखाकर विश्वसनीयता बढ़ाएँ",
      facebook_chat:
        "उपयोगकर्ताओं को अपने क्यूआर कोड से फेसबुक मैसेंजर पर आपसे संपर्क करने की अनुमति दें",
      instagram_chat:
        "उपयोगकर्ताओं को अपनी वेबसाइट से इंस्टाग्राम पर आपसे संपर्क करने की अनुमति दें",
      viber_chat:
        "उपयोगकर्ताओं को अपनी वेबसाइट से सीधे वाइबर पर आपको संदेश भेजने दें",
      paypal: "पेपैल से भुगतान और दान प्राप्त करें",
      youtube: "अपने क्यूआर कोड पर यूट्यूब वीडियो साझा करें",
      mobile_app: "मोबाइल ऐप को बढ़ावा दें और अधिक डाउनलोड प्राप्त करें",
      restaurant_menu:
        "अधिक मेहमानों को आकर्षित करने के लिए अपने क्यूआर कोड पर अपने व्यंजन और पेय प्रस्तुत करें",
      pdf: "देखने और डाउनलोड करने के लिए आसान पीडीएफ साझा करना",
      facebook_review:
        "अपनी साइट पर अपने फेसबुक पेज की समीक्षाएँ प्रदर्शित करें",
      instagram: "अधिक इंस्टाग्राम फॉलोअर्स को आकर्षित करें",
      event: "अपने इवेंट को बढ़ावा दें और आसानी से कैलेंडर आमंत्रण बनाएं",
      spotify: "अपना नवीनतम या पसंदीदा संगीत साझा करें",
      facebook: "अपने क्यूआर कोड में लिंक या फेसबुक लाइक बटन जोड़ें",
      image_gallery:
        "अपने क्यूआर कोड पर अपने स्वामित्व वाले एनएफटी का प्रदर्शन करें",
      faq: "अपने क्यूआर कोड पर सबसे आम प्रश्नों के उत्तर प्रदर्शित करें",
      amazon_review:
        "अपनी साइट पर अमेज़ॅन से अपने उत्पादों या स्टोर के बारे में समीक्षाएँ साझा करें",
      twitter: "अपने ट्वीट्स और ट्विटर फ़ीड दिखाएँ",
      reddit: "अपनी रेडिट प्रोफ़ाइल दिखाएँ",
      line_chat:
        "उपयोगकर्ताओं को अपनी वेबसाइट से सीधे लाइन पर आप तक पहुंचने दें",
      podcasts: "अधिक पॉडकास्ट श्रोता और सब्सक्राइबर प्राप्त करें",
      soundcloud: "साउंडक्लाउड पर अपना संगीत सुनाएं",
      apple_music: "इट्यून्स लाइब्रेरी में गाने साझा करें",
      pinterest: "पिन, बोर्ड और अधिक प्रदर्शित करें",
      twitch: "अधिक स्ट्रीम दर्शक और जुड़ाव प्राप्त करें",
      vimeo: "अपने क्यूआर कोड में विमियो वीडियो जोड़ें",
      music: "संगीत अपलोड करें और अधिक स्ट्रीम प्राप्त करें",
      video: "वीडियो अपलोड करें और उन्हें तुरंत चलाएं",
      file_embed: "अपने दर्शकों के साथ साझा करने के लिए फ़ाइलें एम्बेड करें",
      contact_form: "आगंतुक संपर्क जानकारी और संदेश एकत्र करें",
    },
  },
  th: {
    labels: {
      whatsapp_chat: "แชท WhatsApp",
      click_to_call: "คลิกเพื่อโทร",
      contact_details: "รายละเอียดการติดต่อ",
      google_reviews: "รีวิว Google",
      facebook_chat: "แชท Facebook",
      instagram_chat: "แชท Instagram",
      viber_chat: "แชท Viber",
      paypal: "PayPal",
      youtube: "YouTube",
      mobile_app: "แอปมือถือ",
      restaurant_menu: "เมนูร้านอาหาร",
      pdf: "PDF",
      facebook_review: "รีวิว Facebook",
      instagram: "Instagram",
      event: "กิจกรรม",
      spotify: "Spotify",
      facebook: "Facebook",
      image_gallery: "แกลเลอรี่ภาพ",
      faq: "คำถามที่พบบ่อย",
      amazon_review: "รีวิว Amazon",
      twitter: "Twitter",
      reddit: "Reddit",
      line_chat: "แชท Line",
      podcasts: "พอดแคสต์",
      soundcloud: "SoundCloud",
      apple_music: "Apple Music",
      pinterest: "Pinterest",
      twitch: "Twitch",
      vimeo: "Vimeo",
      music: "เพลง",
      video: "วิดีโอ",
      file_embed: "ฝังไฟล์",
      contact_form: "แบบฟอร์มติดต่อ",
    },
    descriptions: {
      whatsapp_chat:
        "เปิดโอกาสให้ผู้ใช้ติดต่อคุณบน WhatsApp ได้โดยตรงจาก QR code ของคุณ",
      click_to_call:
        "ให้ผู้ใช้มีวิธีที่เร็วที่สุดในการโทรหาคุณโดยตรงจาก QR code ของคุณ",
      contact_details: "แชร์รายละเอียดการติดต่อที่ดาวน์โหลดได้ง่าย",
      google_reviews: "เพิ่มความน่าเชื่อถือโดยแสดงรีวิวจากลูกค้าจริง",
      facebook_chat:
        "อนุญาตให้ผู้ใช้ติดต่อคุณบน Facebook Messenger จาก QR code ของคุณ",
      instagram_chat: "อนุญาตให้ผู้ใช้ติดต่อคุณบน Instagram จากเว็บไซต์ของคุณ",
      viber_chat:
        "ให้ผู้ใช้ส่งข้อความถึงคุณบน Viber ได้โดยตรงจากเว็บไซต์ของคุณ",
      paypal: "รับการชำระเงินและการบริจาคด้วย PayPal",
      youtube: "แชร์วิดีโอ YouTube บน QR code ของคุณ",
      mobile_app: "โปรโมตแอปมือถือและรับการดาวน์โหลดมากขึ้น",
      restaurant_menu:
        "นำเสนออาหารและเครื่องดื่มของคุณบน QR code เพื่อดึงดูดแขกมากขึ้น",
      pdf: "แชร์ PDF ได้ง่ายสำหรับการดูและดาวน์โหลด",
      facebook_review: "แสดงรีวิวจากเพจ Facebook ของคุณบนไซต์ของคุณ",
      instagram: "ดึงดูดผู้ติดตาม Instagram มากขึ้น",
      event: "โปรโมตกิจกรรมของคุณและสร้างคำเชิญในปฏิทินได้อย่างง่ายดาย",
      spotify: "แชร์เพลงล่าสุดหรือเพลงโปรดของคุณ",
      facebook: "เพิ่มลิงก์หรือปุ่มถูกใจ Facebook ไปยัง QR code ของคุณ",
      image_gallery: "แสดง NFT ที่คุณเป็นเจ้าของบน QR code ของคุณ",
      faq: "แสดงคำตอบสำหรับคำถามที่พบบ่อยที่สุดบน QR code ของคุณ",
      amazon_review:
        "แชร์รีวิวเกี่ยวกับผลิตภัณฑ์หรือร้านค้าของคุณจาก Amazon บนไซต์ของคุณ",
      twitter: "แสดงทวีตและฟีด Twitter ของคุณ",
      reddit: "แสดงโปรไฟล์ Reddit ของคุณ",
      line_chat: "ให้ผู้ใช้ติดต่อคุณบน Line ได้จากเว็บไซต์ของคุณ",
      podcasts: "รับผู้ฟังพอดแคสต์และผู้ติดตามมากขึ้น",
      soundcloud: "ให้เพลงของคุณได้ยินบน SoundCloud",
      apple_music: "แชร์เพลงในคลัง iTunes",
      pinterest: "แสดง Pins, บอร์ด และอื่นๆ",
      twitch: "รับผู้ชมสตรีมและการมีส่วนร่วมมากขึ้น",
      vimeo: "เพิ่มวิดีโอ Vimeo ลงใน QR code ของคุณ",
      music: "อัปโหลดเพลงและรับสตรีมมากขึ้น",
      video: "อัปโหลดวิดีโอและเล่นได้ทันที",
      file_embed: "ฝังไฟล์เพื่อแชร์กับผู้ชมของคุณ",
      contact_form: "รวบรวมข้อมูลการติดต่อและข้อความของผู้เยี่ยมชม",
    },
  },
});

const slugToLabel = (slug: string) => {
  return slug
    .split(" ")
    .map((word) => word.charAt(0).toLocaleLowerCase() + word.slice(1))
    .join("_");
};

const { locale } = useI18n();

const getLabels = (label: string) => {
  const slug = slugToLabel(label);

  if (Object.keys(widget_locales.value).includes(locale.value)) {
    try {
      //@ts-ignore
      const x = widget_locales.value[locale.value]["labels"][slug];
      return x;
    } catch (error) {
      return label;
    }
  }
  return label;
};
const getDescription = (label: string, description: string) => {
  const slug = slugToLabel(label);
  if (Object.keys(widget_locales.value).includes(locale.value)) {
    try {
      //@ts-ignore
      const x = widget_locales.value[locale.value]["descriptions"][slug];
      return x;
    } catch (error) {
      return description;
    }
  }
  return description;
};
</script>
